import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, BarChart3, Users, ShoppingCart, Calculator, Globe, VideoIcon,
  Megaphone, GraduationCap, MessageSquare, Eye, Search, Ticket, Shield,
  Link2, Sparkles, Briefcase, Building2, HeartPulse, Plane, Factory,
  Scale, Landmark, Wrench, Cpu, Database, Package, Leaf, BrainCircuit,
  Palette, Code2, Film, Home, Smartphone, Truck,
} from 'lucide-react';
import { allProducts } from '@/data/products';
import { getProductCategoryCounts } from '@/utils/categorySlug';
import { ScrollArea } from '@/components/ui/scroll-area';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'Retail & Fashion': ShoppingCart,
  'Customer Success & CRM': Users,
  'Productivity & Collaboration': BarChart3,
  'Financial & Business Intelligence': Calculator,
  'Core Applications': VideoIcon,
  'Industry Verticals': Building2,
  'Marketing & Sales': Megaphone,
  'Education & Training': GraduationCap,
  'BPO & Contact Centre': MessageSquare,
  'Intelligence & Analytics': Eye,
  'Recruitment': Search,
  'Human Resources': Users,
  'IT & Support': Ticket,
  'Emerging Technologies': Link2,
  'AI & Automation': Bot,
  'Security & Compliance': Shield,
  'Healthcare': HeartPulse,
  'Travel & Hospitality': Plane,
  'Manufacturing': Factory,
  'Legal': Scale,
  'Finance': Landmark,
  'Field Services': Wrench,
  'Beauty & Fashion': Sparkles,
  'Professional Services': Briefcase,
  'Technology': Cpu,
  'Technology Products': Cpu,
  'Data & Insights': Database,
  'Agriculture & Farming': Leaf,
  'AI & Machine Learning': BrainCircuit,
  'Business Applications': Briefcase,
  'Business Tools': Briefcase,
  'Communication': MessageSquare,
  'Creative & Media': Palette,
  'Customer Service': MessageSquare,
  'Data Analytics & BI': Database,
  'Developer Tools': Code2,
  'Entertainment & Media': Film,
  'Facilities Management': Building2,
  'Health & Beauty': Sparkles,
  'Healthcare & Medicine': HeartPulse,
  'Hospitality & Travel': Plane,
  'Legal & Compliance': Scale,
  'Manufacturing & Industrial': Factory,
  'Media & Publishing': Film,
  'Mobile Development': Smartphone,
  'Operations': Package,
  'Operations & Compliance': Shield,
  'Operations & Logistics': Truck,
  'Operations & Quality': Shield,
  'Real Estate & Property': Home,
  'Transportation & Logistics': Truck,
};

export const FooterProductLinks = () => {
  const categories = useMemo(() => {
    return getProductCategoryCounts()
      .map(({ name, count, slug }) => ({
        name,
        count,
        href: `/products#${slug}`,
        Icon: ICON_MAP[name] || Package,
      }));
  }, []);

  return (
    <div>
      <h3 className="text-base font-semibold text-white mb-4">Product Categories</h3>
      <ScrollArea className="h-80 pr-3" type="always">
        <ul className="space-y-2 pr-4">
          <li>
            <Link
              to="/products"
              className="group flex min-h-0 items-center justify-between py-0.5 text-sm leading-5 text-gray-300 transition-colors hover:text-blue-400"
            >
              <span className="flex items-center min-w-0">
                <Globe className="h-4 w-4 mr-3 group-hover:text-blue-400 flex-shrink-0" />
                <span className="truncate">All Products</span>
              </span>
              <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{allProducts.length}</span>
            </Link>
          </li>
          <li>
            <Link
              to="/ai-agents"
              className="group flex min-h-0 items-center py-0.5 text-sm leading-5 text-gray-300 transition-colors hover:text-blue-400"
            >
              <Bot className="h-4 w-4 mr-3 group-hover:text-blue-400 flex-shrink-0" />
              <span className="truncate">AI Agents</span>
            </Link>
          </li>
          {categories.map(({ name, count, href, Icon }) => (
            <li key={name}>
              <Link
                to={href}
                className="group flex min-h-0 items-center justify-between py-0.5 text-sm leading-5 text-gray-300 transition-colors hover:text-blue-400"
              >
                <span className="flex items-center min-w-0">
                  <Icon className="h-4 w-4 mr-3 group-hover:text-blue-400 flex-shrink-0" />
                  <span className="truncate">{name}</span>
                </span>
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </ScrollArea>

      <div className="mt-4 text-center">
        <Link to="/products" className="inline-flex min-h-0 items-center py-0.5 text-xs leading-5 text-gray-400 transition-colors hover:text-blue-400">
          View all {allProducts.length} products →
        </Link>
      </div>

      <div className="mt-4">
        <Link to="/website-wizard" className="inline-flex min-h-0 items-center py-0.5 text-xs leading-5 text-blue-400 transition-colors hover:text-blue-300">
          Launch Project Wizard →
        </Link>
      </div>
    </div>
  );
};
